import { Capacitor } from '@capacitor/core';
import { inject, Inject, Injectable, Injector } from '@angular/core';
import { combineLatest, from, Observable, of, throwError, timer, zip } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

// import {AngularFireAuth} from '@angular/fire/compat/auth';
import { SignInResult } from '@capacitor-firebase/authentication';

import { UserService } from '@core/services/user/user.service';
import { NativeService } from '@core/services/native/native.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';

import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';

import { ErrorsCode } from '@config/errors.config';
import { ERRORS } from '@config/labels/errors.labels';
import { DoodUserModel } from '@store/authentication/authentication.model';
import { AuthStoreDispatcher } from '@common/dispatchers/authentication.dispatcher';

import {
  browserPopupRedirectResolver,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  FacebookAuthProvider,
  fetchSignInMethodsForEmail,
  getAdditionalUserInfo,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updatePassword,
  User,
  UserCredential,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { Auth, authState, idToken } from '@angular/fire/auth';
import { isDoodAnonymousJwt, isTokenExpired } from '@core/helpers/dood-anonymous-jwt.helper';
import { AnonymousUpgradeSessionService } from '@core/services/auth/anonymous-upgrade-session.service';
import { shouldRestoreSessionUser } from '@core/helpers/session-identity.helper';

@Injectable({
  providedIn: 'root',
})
export class AuthFirebaseService {
  private auth: Auth = inject(Auth);
  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly anonymousUpgradeSession = inject(AnonymousUpgradeSessionService);
  idToken$ = idToken(this.auth);

  private readonly isAuthSynced$ = combineLatest({
    // Sometimes after a long period of time, the user seems to be desynchronized from the firebase authentication and the authentication store.
    // This function checks if the user is connected to firebase and the authentication store. If not, it tries to reconnect the user.
    // When desync is detected, we now attempt to refresh the token before disconnecting the user for better UX.
    firebaseIdToken: this.idToken$,
    user: this.authSelector.selectUser$,
    storeStatus: this.authSelector.selectStatus$,
  }).pipe(
    take(1),
    switchMap(({ firebaseIdToken, user, storeStatus }) => {
      // CB2 preview masks auth as disconnected; a lingering Firebase session would
      // look like a desync and trigger restoreUser$ → GET /users/me for nothing.
      if (this.cb2PreviewState.isActive()) {
        return of(true);
      }

      // Dood guest sessions use a JWT in the store with no Firebase user — not a desync.
      if (this.isDoodAnonymousSession(storeStatus.token)) {
        return of(true);
      }

      const userNotConnected = !firebaseIdToken && !user;
      if (userNotConnected) {
        return of(true);
      }

      const isTokenSynced = firebaseIdToken === storeStatus.token;
      const isConnectionSynced = !!firebaseIdToken && !!user && isTokenSynced;

      if (isConnectionSynced) {
        return of(true);
      }

      if (!isTokenSynced && firebaseIdToken && !isDoodAnonymousJwt(storeStatus.token)) {
        this.setStoreFirebaseToken(firebaseIdToken);
        return of(true);
      }
      const isConnectedOnFirebase = !!firebaseIdToken;
      const isConnectedOnAuthenticationStore = !!user;
      if (!isConnectedOnFirebase && isConnectedOnAuthenticationStore) {
        // Try to refresh the token before disconnecting
        console.log('[Auth] Token desync detected. Attempting to refresh token...');
        return from(this.auth.currentUser?.getIdToken(true) ?? Promise.resolve(null)).pipe(
          switchMap(refreshedToken => {
            if (refreshedToken) {
              console.log('[Auth] ✅ Token refreshed successfully');
              this.setStoreFirebaseToken(refreshedToken);
              return of(true);
            } else {
              console.log('[Auth] ❌ Cannot refresh token, disconnecting...');
              return from(this.disconnectUser()).pipe(map(() => true));
            }
          }),
          catchError(() => {
            console.log('[Auth] ❌ Error refreshing token, disconnecting...');
            return from(this.disconnectUser()).pipe(map(() => true));
          }),
        );
      }
      if (isConnectedOnFirebase && !isConnectedOnAuthenticationStore) {
        return this.restoreUser$(firebaseIdToken!).pipe(map(() => true));
      }

      return of(true);
    }),
    catchError(() => {
      return this.disconnectUser();
    }),
    catchError(() => {
      return of(true);
    }),
  );

  authState$ = this.isAuthSynced$.pipe(
    switchMap(() => authState(this.auth)),
    shareReplay(1),
  );

  private _isAnonymous = false;

  passwordReset = false;
  firebaseToken$ = this.authSelector.selectStatusToken;

  get isAnonymous(): boolean {
    return this._isAnonymous;
  }

  constructor(
    private authSelector: AuthStoreSelector,
    private readonly userService: UserService,
    private authDispatcher: AuthStoreDispatcher,
    private readonly modalsService: ModalsService,
    private readonly nativeService: NativeService,
    private settingsSelector: SettingsStoreSelector,
    private readonly analyticsService: AnalyticsService,
    @Inject(Injector) private readonly injector: Injector,
  ) {
    let previousFirebaseAuthenticationStatus = false;

    // Drop legacy Firebase anonymous sessions — guests now use Dood JWTs only.
    this.authState$
      .pipe(
        take(1),
        switchMap(firebaseUser =>
          firebaseUser?.isAnonymous ? from(signOut(this.auth)).pipe(map(() => null)) : of(null),
        ),
      )
      .subscribe();

    zip(this.isAuthenticate$(), this.authSelector.selectUserId)
      .pipe(
        switchMap(([firebaseAuthenticationStatus, userId]) => {
          if (this.cb2PreviewState.isActive()) {
            previousFirebaseAuthenticationStatus = !!firebaseAuthenticationStatus;
            return of(previousFirebaseAuthenticationStatus);
          }

          // Guest with a valid Dood JWT: reload profile, never treat missing Firebase as invalid.
          if (this.isDoodAnonymousSession()) {
            if (userId) {
              return this.userService.loadUser$().pipe(
                catchError(err => {
                  console.log('[Auth][Error] Anonymous Dood user not found. Clearing session...');
                  this.disconnectUser().then(() => {
                    document.location.reload();
                  });
                  return throwError(() => err);
                }),
                tap(user => {
                  console.log('[Auth] Logged in anonymous Dood user', { user });
                }),
              );
            }
            return of(true);
          }

          const isFirebaseSessionTerminated =
            previousFirebaseAuthenticationStatus && !firebaseAuthenticationStatus;
          const isInvalidState = !firebaseAuthenticationStatus && !!userId;

          if (userId) {
            return this.userService.loadUser$().pipe(
              catchError(err => {
                console.log('[Auth][Error] User not found on DOOD. Logout...');
                this.disconnectUser().then(() => {
                  document.location.reload();
                });

                return throwError(() => err);
              }),
              tap(user => {
                console.log('[Auth] Logged in user', { user });
              }),
            );
          }

          if (isFirebaseSessionTerminated || isInvalidState) {
            console.log(
              '[Auth] Session terminated or invalid state. Attempting to refresh token...',
            );

            // Try to refresh the token first
            return from(this.auth.currentUser?.getIdToken(true) ?? Promise.resolve(null)).pipe(
              switchMap(refreshedToken => {
                if (refreshedToken) {
                  console.log('[Auth] ✅ Token refreshed successfully after session issue');
                  this.setStoreFirebaseToken(refreshedToken);
                  previousFirebaseAuthenticationStatus = true;

                  // Try to reload user data
                  return this.loadUser$().pipe(
                    map(() => true),
                    catchError(() => {
                      // If we can't load user, just disconnect (guards will handle modal + navigation)
                      console.log('[Auth] Cannot load user after refresh, disconnecting...');
                      this.disconnectUser().then(() => {
                        console.log('[Auth] User disconnected. Guards will handle authentication.');
                      });
                      previousFirebaseAuthenticationStatus = false;
                      return of(false);
                    }),
                  );
                } else {
                  // Cannot refresh token, just disconnect (guards will handle modal + navigation)
                  console.log('[Auth] ❌ Cannot refresh token, disconnecting...');
                  this.disconnectUser().then(() => {
                    console.log('[Auth] User disconnected. Guards will handle authentication.');
                  });
                  previousFirebaseAuthenticationStatus = false;
                  return of(false);
                }
              }),
              catchError(() => {
                console.log('[Auth] ❌ Error refreshing token, disconnecting...');
                this.disconnectUser().then(() => {
                  console.log('[Auth] User disconnected. Guards will handle authentication.');
                });
                previousFirebaseAuthenticationStatus = false;
                return of(false);
              }),
            );
          }
          previousFirebaseAuthenticationStatus = !!firebaseAuthenticationStatus;
          return of(previousFirebaseAuthenticationStatus);
        }),
      )
      .subscribe();

    // TODO: Later add takeUntilDestroyed
    this.authSelector.selectUserIsAnonymous.subscribe(isAnonymous => {
      this._isAnonymous = !!isAnonymous;
    });
  }

  checkIfUserExists(email: string): Promise<boolean> {
    email = email.toLowerCase();
    return fetchSignInMethodsForEmail(this.auth, email).then((res: string | string[]) => {
      return res.indexOf('password') > -1 || res.indexOf('google.com') > -1;
    });
  }

  fetchSignInMethodsForEmail(email: string): Promise<string[]> {
    email = email.toLowerCase();
    return fetchSignInMethodsForEmail(this.auth, email);
  }

  signInWithPassword(email: string, password: string): Observable<DoodUserModel | undefined> {
    email = email.toLowerCase();
    return from(signInWithEmailAndPassword(this.auth, email, password)).pipe(
      tap(credentials => this.saveFirebaseTokenInSessionStore$(credentials).subscribe()),
      switchMap(userCredential => this.saveFirebaseTokenInSessionStore$(userCredential)),
      switchMap(() => this.loadUser$()),
    );
  }

  signInWithFacebook(): Observable<UserCredential> {
    return this.signInWithPopup(new FacebookAuthProvider()).pipe(
      catchError(error => {
        if (error.code === 'auth/popup-blocked') {
          return this.signInWithRedirect(new FacebookAuthProvider());
        } else {
          return throwError(() => error);
        }
      }),
    );
  }

  signInWithGoogle(): Observable<UserCredential> {
    if (this.nativeService.isInAppBrowser()) {
      return this.signInWithRedirect(new GoogleAuthProvider());
    }

    return this.signInWithPopup(new GoogleAuthProvider()).pipe(
      catchError(error => {
        if (error.code === 'auth/popup-blocked') {
          return this.signInWithRedirect(new GoogleAuthProvider());
        } else {
          return throwError(() => error);
        }
      }),
    );
  }

  signInWithApple(): Observable<UserCredential> {
    const provider = this.createAppleAuthProvider();
    if (Capacitor.isNativePlatform()) {
      return this.signInWithCredentials(provider);
    }
    return this.signInWithPopup(provider).pipe(
      catchError(error => {
        if (error.code === 'auth/popup-blocked') {
          return this.signInWithRedirect(provider);
        } else {
          return throwError(() => error);
        }
      }),
    );
  }

  createAppleAuthProvider(): OAuthProvider {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    return provider;
  }

  signInWithCustomToken(token: string): Observable<DoodUserModel | undefined> {
    return from(signInWithCustomToken(this.auth, token)).pipe(
      switchMap((userCredential: UserCredential) =>
        this.saveFirebaseTokenInSessionStore$(userCredential),
      ),
      switchMap(() => this.loadUser$()),
    );
  }

  private restoreUser$(firebaseToken: string) {
    this.setStoreFirebaseToken(firebaseToken);
    return this.loadUser$();
  }

  private isDoodAnonymousSession(
    token: string | null = this.authSelector.selectStatus().token,
  ): boolean {
    if (isTokenExpired(this.authSelector.selectStatus().tokenExpiresAt)) {
      return false;
    }
    return isDoodAnonymousJwt(token);
  }

  /** Write a Firebase ID token to the store, unless a Dood guest upgrade is in progress. */
  private setStoreFirebaseToken(token: string | null): void {
    if (this.anonymousUpgradeSession.isPreserving) {
      this.anonymousUpgradeSession.restore();
      return;
    }
    this.authDispatcher.updateStatus({ token, tokenExpiresAt: null });
  }

  private getFirebaseIdTokenFromCredential$(userCredential: UserCredential): Observable<string> {
    return from(userCredential.user.getIdToken()).pipe(
      tap(() => this.anonymousUpgradeSession.restore()),
    );
  }

  private signInWithPopup(provider: any): Observable<UserCredential> {
    return from(signInWithPopup(this.auth, provider, browserPopupRedirectResolver)).pipe(
      catchError(error => {
        return throwError(() => error);
      }),
    );
  }

  private signInWithRedirect(provider: any): Observable<UserCredential> {
    this.authDispatcher.updateStatus({ redirect: true });
    return from(
      signInWithRedirect(this.auth, provider).then(() => {
        return getRedirectResult(this.auth);
      }),
    ).pipe(
      switchMap(userCredential =>
        userCredential ? of(userCredential) : throwError(() => new Error('UserCredential is null')),
      ),
    );
  }

  private signInWithCredentials(provider: any): Observable<UserCredential> {
    return from(signInWithCredential(this.auth, provider));
  }

  /**
   * Start a timer that clears the Dood guest session at JWT expiry (no refresh endpoint).
   */
  startAnonymousUserLogoutTimer(): void {
    const expiresAt = this.authSelector.selectStatus().tokenExpiresAt;
    if (!expiresAt) {
      return;
    }

    const delayMs = Math.max(expiresAt - Date.now(), 0);
    timer(delayMs)
      .pipe(
        take(1),
        switchMap(() => from(this.disconnectUser())),
      )
      .subscribe();
  }

  /**
   * Create a real Firebase email/password account for upgrading a Dood guest.
   * Preserves the Dood JWT in the store until AnonymousUpgradeSessionService.finish().
   */
  upgradeAnonymousWithEmailAndPassword(email: string, password: string): Observable<string> {
    this.anonymousUpgradeSession.begin();
    return from(createUserWithEmailAndPassword(this.auth, email.toLowerCase(), password)).pipe(
      switchMap(userCredential => this.getFirebaseIdTokenFromCredential$(userCredential)),
      catchError(error => {
        this.anonymousUpgradeSession.cancel();
        return throwError(() => error);
      }),
    );
  }

  /**
   * Sign in with Google (real account) while keeping the Dood guest JWT for the upgrade call.
   * {@link AnonymousUpgradeSessionService.finish} is called after a successful PATCH upgrade.
   */
  upgradeAnonymousWithGoogle(): Observable<{
    firebaseIdToken: string;
    userCredential: UserCredential;
  }> {
    this.anonymousUpgradeSession.begin();
    return this.signInWithGoogle().pipe(
      switchMap(userCredential =>
        this.getFirebaseIdTokenFromCredential$(userCredential).pipe(
          map(firebaseIdToken => ({ firebaseIdToken, userCredential })),
        ),
      ),
      catchError(error => {
        this.anonymousUpgradeSession.cancel();
        return throwError(() => error);
      }),
    );
  }

  upgradeAnonymousWithApple(): Observable<{
    firebaseIdToken: string;
    userCredential: UserCredential;
  }> {
    this.anonymousUpgradeSession.begin();
    return this.signInWithApple().pipe(
      switchMap(userCredential =>
        this.getFirebaseIdTokenFromCredential$(userCredential).pipe(
          map(firebaseIdToken => ({ firebaseIdToken, userCredential })),
        ),
      ),
      catchError(error => {
        this.anonymousUpgradeSession.cancel();
        return throwError(() => error);
      }),
    );
  }

  upgradeAnonymousWithFacebook(): Observable<{
    firebaseIdToken: string;
    userCredential: UserCredential;
  }> {
    this.anonymousUpgradeSession.begin();
    return this.signInWithFacebook().pipe(
      switchMap(userCredential =>
        this.getFirebaseIdTokenFromCredential$(userCredential).pipe(
          map(firebaseIdToken => ({ firebaseIdToken, userCredential })),
        ),
      ),
      catchError(error => {
        this.anonymousUpgradeSession.cancel();
        return throwError(() => error);
      }),
    );
  }

  signUp(email: string, password: string): Promise<UserCredential> {
    email = email.toLowerCase();
    return createUserWithEmailAndPassword(this.auth, email, password).then(async res => {
      const firebaseToken = await res.user?.getIdToken();
      if (firebaseToken) {
        this.setStoreFirebaseToken(firebaseToken);
      }
      return res;
    });
  }

  getFirebaseToken$(userCredential: UserCredential): Observable<string | undefined> {
    const additionalUserInfo = getAdditionalUserInfo(userCredential);
    if (!!additionalUserInfo?.isNewUser) {
      return of(undefined);
    }

    return this.idToken$.pipe(
      switchMap((firebaseToken: any) => {
        if (firebaseToken) {
          return of(firebaseToken);
        }
        return of(undefined);
      }),
    );
  }

  saveFirebaseTokenInSessionStore$(userCredential: UserCredential): Observable<boolean> {
    const user = userCredential.user;
    if (!user) {
      return of(false);
    }

    return this.idToken$.pipe(
      tap((firebaseToken: string | null) => this.setStoreFirebaseToken(firebaseToken)),
      map(() => true),
    );
  }

  saveNativeFirebaseTokenInSessionStore$(userCredential: SignInResult): Observable<boolean> {
    const user = userCredential.user;
    if (!user) {
      return of(false);
    }

    return this.idToken$.pipe(
      tap(token => this.setStoreFirebaseToken(token)),
      map(() => true),
    );
  }

  loadUser$(): Observable<DoodUserModel | undefined> {
    return this.userService.loadUser$().pipe(
      catchError(() => {
        return of(undefined);
      }),
    );
  }

  resetPassword(email: string): Promise<boolean> {
    email = email.toLowerCase();
    /* Set language code for email template */
    const language = this.settingsSelector.app.locale;
    if (language) {
      this.auth.languageCode = language;
    }

    return sendPasswordResetEmail(this.auth, email)
      .then(async () => {
        return true;
      })
      .catch((_err: FirebaseError) => {
        return false;
      });
  }

  /**
   * The user may be authenticated to firebase, but it may not be loaded !
   * (Our Authentication store can have a valid firebase token, but the user is not loaded in the store)
   *
   * Dood guest sessions have a JWT in the store without a Firebase user — do not clear them here.
   */
  isAuthenticate$(): Observable<User | null> {
    return this.idToken$.pipe(
      take(1),
      tap(token => {
        if (token) {
          // Never overwrite a Dood anonymous JWT with a leftover Firebase anonymous token.
          if (this.auth.currentUser?.isAnonymous) {
            return;
          }
          this.setStoreFirebaseToken(token);
          return;
        }

        const storeToken = this.authSelector.selectStatus().token;
        if (storeToken && isDoodAnonymousJwt(storeToken)) {
          // Valid (or soon-checked) Dood guest session — leave the store alone.
          return;
        }

        if (storeToken) {
          this.authDispatcher.updateStatus({ token: null, tokenExpiresAt: null });
        }
      }),
      switchMap(() => this.authState$),
    );
  }

  /**
   * Reload the store user from /users/me when a real Firebase session exists but the store has
   * no user or a guest (see shouldRestoreSessionUser). Call after isAuthenticate$(), which puts
   * the Firebase ID token in the store.
   */
  restoreSessionUser$(firebaseUser: User | null): Observable<unknown> {
    if (this.cb2PreviewState.isActive() || this.anonymousUpgradeSession.isPreserving) {
      return of(null);
    }
    const storeUser = this.authSelector.selectUser();
    if (!shouldRestoreSessionUser(firebaseUser, storeUser)) {
      return of(null);
    }
    this.analyticsService.log({
      level: 'warn',
      label: 'auth_session_user_restored',
      message: {
        storeUserId: storeUser?.id ?? null,
        storeUserIsAnonymous: storeUser?.is_anonymous ?? null,
      },
    });
    return this.loadUser$();
  }

  /** True when the store holds a non-expired Dood anonymous JWT (guest session). */
  hasDoodAnonymousSession(): boolean {
    return this.isDoodAnonymousSession();
  }

  disconnectUser(): Promise<void> {
    return signOut(this.auth).then(() => {
      this.authDispatcher.resetAndClear();
    });
  }

  public changePassword(oldPassword: string, newPassword: string): void {
    this.analyticsService.trackEvent('change_password_intent');
    const user = this.auth.currentUser;
    if (user?.email) {
      const credentials = EmailAuthProvider.credential(user.email, oldPassword);

      this.reauthenticateWithCredential(credentials, newPassword);
    }
  }

  public reauthenticateWithCredential(credentials: any, newPassword: string): void {
    const user = this.auth.currentUser;
    if (user) {
      reauthenticateWithCredential(user, credentials)
        .then(() => {
          updatePassword(user, newPassword).then(() => {
            this.analyticsService.trackEvent('change_password');
          });
        })
        .catch(error => {
          this.analyticsService.trackEvent('change_password_failure');
          console.error(error);
        });
    }
  }

  public getEmailErrorMessage(error: FirebaseError): string | undefined {
    switch (error.code) {
      case ErrorsCode.EmailAlreadyExist:
        return ERRORS.EMAIL_ALREADY_EXIST;
      case ErrorsCode.InvalidEmail:
        return ERRORS.LOGIN_INVALID;
      case ErrorsCode.UserNotFound:
        return ERRORS.USER_NOT_FOUND;
      case ErrorsCode.WeakPassword:
        return ERRORS.WEAK_PASSWORD;
      case ErrorsCode.WrongPassword:
        return undefined;
      case ErrorsCode.DisabledUser:
        return ERRORS.DISABLED_USER;
      default:
      // console.error(error?.message);
    }
    return undefined;
  }

  public getPasswordErrorMessage(error: unknown): string | undefined {
    const code = (error as { code?: unknown })?.code;
    switch (code) {
      case ErrorsCode.WeakPassword:
        return ERRORS.WEAK_PASSWORD;
      case ErrorsCode.WrongPassword:
        return ERRORS.PASSSWORD_INVALID;
      default:
      // console.error(error?.message);
    }
    return undefined;
  }

  initAuthTokenRefresh(): void {
    this.authState$.subscribe(user => {
      if (user?.isAnonymous) {
        // Legacy Firebase anonymous — sign out; guests use Dood JWTs.
        signOut(this.auth);
        return;
      }

      if (user) {
        this.idToken$.pipe(take(1)).subscribe(token => {
          this.startTokenRefreshTimer();
          this.setStoreFirebaseToken(token);
        });
        return;
      }

      // No Firebase user: keep Dood guest sessions; otherwise clear.
      if (this.isDoodAnonymousSession()) {
        return;
      }
      this.authDispatcher.resetAndClear();
    });
  }

  private startTokenRefreshTimer(): void {
    // Refresh token each 10 minutes
    timer(600000)
      .pipe(
        take(1),
        tap(() => console.log('[Firebase] startTokenRefreshTimer tick')),
        switchMap(() => this.authState$),
        switchMap(v => (v && !v.isAnonymous ? v.getIdTokenResult(true) : of(undefined))),
      )
      .subscribe(result => {
        if (result) {
          this.setStoreFirebaseToken(result.token);
          // TODO: Check WTF is this...
          this.startTokenRefreshTimer();
        } else if (!this.isDoodAnonymousSession()) {
          // TODO: Check if we disconnect user here
          this.authDispatcher.resetAndClear();
        }
      });
  }

  getCurrentUser(): User | null {
    return this.auth.currentUser;
  }
}
